import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { subcontractsApi } from '@/services/api'
import { getErrorMessage } from '@/utils/apiError'

const normalize = text =>
  String(text ?? '')
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()

const matchesSearch = (work, query) => {
  if (!query) {
    return true
  }
  const haystack = [
    work.code,
    work.name,
    work.project?.code,
    work.project?.name,
    work.contractor?.name
  ]
    .map(normalize)
    .join(' ')
  return haystack.includes(normalize(query))
}

// Las fechas llegan como YYYY-MM-DD, igual que el valor de un <input type="date">.
const inDateRange = (date, from, to) => {
  if (!from && !to) {
    return true
  }
  if (!date) {
    return false
  }
  return (!from || date >= from) && (!to || date <= to)
}

const projectKey = work => work.project?.code || ''

export const useSubcontractsStore = defineStore('subcontracts', () => {
  // null = todavía no se sabe si el Portal de Compras está conectado
  const enabled = ref(null)

  const items = ref([])
  const truncated = ref(false)
  const isLoading = ref(false)
  const error = ref(null)
  const hasLoaded = ref(false)

  const searchQuery = ref('')
  const statusFilter = ref('')
  const projectFilter = ref('')
  const startFrom = ref('')
  const startTo = ref('')
  const onlyNew = ref(false)
  const selectedSupplierId = ref(null)

  const current = ref(null)
  const isLoadingDetail = ref(false)
  const detailError = ref(null)

  const projectOptions = computed(() => {
    const byCode = new Map()
    for (const work of items.value) {
      if (work.project?.code && !byCode.has(work.project.code)) {
        byCode.set(work.project.code, work.project.name || work.project.code)
      }
    }
    return [...byCode.entries()]
      .map(([code, name]) => ({ code, name }))
      .sort((a, b) => a.name.localeCompare(b.name, 'es'))
  })

  const hasFilters = computed(() =>
    Boolean(
      searchQuery.value ||
        statusFilter.value ||
        projectFilter.value ||
        startFrom.value ||
        startTo.value ||
        onlyNew.value
    )
  )

  const filteredItems = computed(() =>
    items.value.filter(
      work =>
        matchesSearch(work, searchQuery.value) &&
        (!statusFilter.value || work.status === statusFilter.value) &&
        (!projectFilter.value || projectKey(work) === projectFilter.value) &&
        inDateRange(work.firstActualStartDate, startFrom.value, startTo.value) &&
        (!onlyNew.value || work.newLines > 0)
    )
  )

  const fetchStatus = async () => {
    try {
      const response = await subcontractsApi.getStatus()
      enabled.value = Boolean(response.data?.enabled)
    } catch {
      enabled.value = false
    }
    return enabled.value
  }

  const fetchAll = async () => {
    isLoading.value = true
    error.value = null
    try {
      const params = selectedSupplierId.value ? { supplierId: selectedSupplierId.value } : {}
      const response = await subcontractsApi.getAll(params)
      items.value = response.data?.items || []
      truncated.value = Boolean(response.data?.truncated)
      hasLoaded.value = true
    } catch (err) {
      error.value = getErrorMessage(err, 'No se pudieron cargar tus subcontratos. Intenta de nuevo')
    } finally {
      isLoading.value = false
    }
  }

  const fetchDetail = async id => {
    isLoadingDetail.value = true
    detailError.value = null
    current.value = null
    try {
      const response = await subcontractsApi.getById(id)
      current.value = response.data
    } catch (err) {
      detailError.value = getErrorMessage(err, 'No se pudo cargar el subcontrato. Intenta de nuevo')
    } finally {
      isLoadingDetail.value = false
    }
  }

  const clearFilters = () => {
    searchQuery.value = ''
    statusFilter.value = ''
    projectFilter.value = ''
    startFrom.value = ''
    startTo.value = ''
    onlyNew.value = false
  }

  return {
    enabled,
    items,
    truncated,
    isLoading,
    error,
    hasLoaded,
    searchQuery,
    statusFilter,
    projectFilter,
    startFrom,
    startTo,
    onlyNew,
    projectOptions,
    hasFilters,
    selectedSupplierId,
    filteredItems,
    current,
    isLoadingDetail,
    detailError,
    fetchStatus,
    fetchAll,
    fetchDetail,
    clearFilters
  }
})
